import { Area, AreaChart, Bar, BarChart, Line, LineChart, XAxis } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart';
import { formatBucketLabel } from './chartDate';
import type { ChartStyle } from './chartPrefs';
import { formatMetricValue, type TrendChartData } from './trendSeries';

// Compact Kyck-green mini chart for a hero card — same visual language + style
// (bar/line/area) as the big chart, no axes, taller than a sparkline. Interactive:
// a hover tooltip shows the bucket's value + date label (reuses the big chart's
// ChartTooltipContent). Clicks bubble to the enclosing card (metric selection).
//
// The axes/tooltip/series are DIRECT children of the chart element (never wrapped
// in a Fragment): Recharts discovers graphical children via React.Children.forEach,
// which does NOT descend into fragments — a fragment-wrapped Bar/Line/Area renders
// blank. The chart type is switched with inline conditional JSX for the same reason.
export function ReportingMiniChart({ data, style }: { data: TrendChartData; style: ChartStyle }) {
  if (data.isEmpty) return <div className="h-16" aria-hidden />;

  const config: ChartConfig = { value: { label: data.metric.label, color: 'hsl(var(--chart-1))' } };
  const name = data.metric.label;

  // Shared as single ELEMENTS (not a fragment) so they stay direct children.
  const xAxis = <XAxis dataKey="bucket" hide />; // hidden — feeds the tooltip its bucket label
  const tooltip = (
    <ChartTooltip
      cursor={{ fill: 'hsl(var(--muted))', opacity: 0.35 }}
      content={
        <ChartTooltipContent
          labelFormatter={(v) => formatBucketLabel(String(v), data.grouping)}
          formatter={(value) => (
            <span className="font-mono font-medium tabular-nums text-foreground">
              {formatMetricValue(data.format, Number(value), data.currency)}
            </span>
          )}
        />
      }
    />
  );
  const common = { data: data.rows, margin: { top: 2, right: 0, bottom: 0, left: 0 } };

  return (
    <ChartContainer config={config} className="aspect-auto h-16 w-full">
      {style === 'line' ? (
        <LineChart {...common}>
          {xAxis}
          {tooltip}
          <Line
            type="monotone"
            dataKey="value"
            name={name}
            stroke="var(--color-value)"
            dot={false}
            strokeWidth={2}
            isAnimationActive={false}
          />
        </LineChart>
      ) : style === 'area' ? (
        <AreaChart {...common}>
          {xAxis}
          {tooltip}
          <Area
            type="monotone"
            dataKey="value"
            name={name}
            stroke="var(--color-value)"
            fill="var(--color-value)"
            fillOpacity={0.18}
            strokeWidth={2}
            isAnimationActive={false}
          />
        </AreaChart>
      ) : (
        <BarChart {...common}>
          {xAxis}
          {tooltip}
          <Bar dataKey="value" name={name} fill="var(--color-value)" radius={1} isAnimationActive={false} />
        </BarChart>
      )}
    </ChartContainer>
  );
}
